import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { FiDownload, FiLoader } from 'react-icons/fi'
import {
  uploadShipmentDocuments,
  getDocumentsByEntity,
  downloadDocumentById,
  downloadMultipleDocuments,
  openDocumentById,
  type DocumentListItem,
} from '@/services/api/documents'
import { getStatusBadgeFromBackend } from '@/services/api/shipments'
import AISummaryModal from '@/components/ui/AISummaryModal'
import AIRecommendationsPanel from '@/components/ui/AIRecommendationsPanel'

interface ShipmentDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  shipmentData?: {
    id?: string
    buyContract?: string
    saleContract?: string
    booking?: string
    billOfLading: string
    containerNumber: string
    containerNumbers?: string[]
    vessel: string
    shippingLine: string
    product: string
    customer: string
    factory?: string
    origin: string
    destination: string
    etd: string
    eta: string
    status: string
  }
  onDocumentUploaded?: () => void
}

const ShipmentDetailsModal = ({ isOpen, onClose, shipmentData, onDocumentUploaded }: ShipmentDetailsModalProps) => {
  const [uploadFiles, setUploadFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [fileInputKey, setFileInputKey] = useState(0)
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false)
  const [documents, setDocuments] = useState<DocumentListItem[]>([])
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set())
  const [downloadingDocs, setDownloadingDocs] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const loadDocuments = useCallback(async () => {
    if (!shipmentData?.id) {
      setDocuments([])
      return
    }
    setDocumentsLoading(true)
    try {
      const docs = await getDocumentsByEntity('shipment', shipmentData.id)
      setDocuments(docs)
      setSelectedDocIds(new Set())
    } catch {
      setDocuments([])
    } finally {
      setDocumentsLoading(false)
    }
  }, [shipmentData?.id])

  useEffect(() => {
    if (!isOpen) {
      setUploadFiles([])
      setUploadError(null)
      setUploadSuccess(false)
      setSelectedDocIds(new Set())
      setDownloadError(null)
      return
    }
    loadDocuments()
  }, [isOpen, loadDocuments])

  const toggleDocSelection = (docId: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev)
      if (next.has(docId)) next.delete(docId)
      else next.add(docId)
      return next
    })
  }

  const toggleSelectAllDocs = () => {
    if (selectedDocIds.size === documents.length) {
      setSelectedDocIds(new Set())
    } else {
      setSelectedDocIds(new Set(documents.map((d) => d.id)))
    }
  }

  const handleDownloadSelected = async () => {
    const selected = documents.filter((d) => selectedDocIds.has(d.id))
    if (selected.length === 0) return
    setDownloadingDocs(true)
    setDownloadError(null)
    try {
      await downloadMultipleDocuments(selected)
    } catch (err: unknown) {
      setDownloadError((err as Error)?.message ?? 'Download failed')
    } finally {
      setDownloadingDocs(false)
    }
  }

  const handleDownloadOne = async (doc: DocumentListItem) => {
    setDownloadError(null)
    try {
      await downloadDocumentById(doc.id, doc.name)
    } catch (err: unknown) {
      setDownloadError((err as Error)?.message ?? 'Download failed')
    }
  }

  if (!isOpen || !shipmentData) return null

  const statusBadgeProps = getStatusBadgeFromBackend(shipmentData.status)
  const statusPill = (
    <span
      className="inline-flex items-center justify-center rounded px-2 py-1"
      style={{
        backgroundColor: '#EAF1FF',
        color: '#2C71F6',
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '18px'
      }}
    >
      {statusBadgeProps.label}
    </span>
  )

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '548px',
          maxWidth: '548px',
          height: '100vh',
          maxHeight: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0 px-4 sm:px-6"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <h2 
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            Shipment Details
          </h2>
          <div className="flex items-center gap-2">
            <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ 
              width: '24px',
              height: '24px'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10.5"
                stroke="#212126"
                strokeWidth="1.5"
                fill="none"
              />
              <line
                x1="9.17"
                y1="9.17"
                x2="14.83"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <line
                x1="14.83"
                y1="9.17"
                x2="9.17"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ minHeight: 0 }}>
          {shipmentData.id && (
            <div className="mb-4">
              <AIRecommendationsPanel
                entityType="shipment"
                entityId={shipmentData.id}
                agentType="SHIPMENT_AGENT"
              />
            </div>
          )}
          <div className="space-y-0">
            {/* Buy Contract */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Buy Contract
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {shipmentData.buyContract || '—'}
              </span>
            </div>

            {/* Sale Contract */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Sale Contract
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {shipmentData.saleContract || '—'}
              </span>
            </div>

            {/* Booking */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Booking
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {shipmentData.booking || '-'}
              </span>
            </div>

            {/* Bill of Lading */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Bill of Lading
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.billOfLading || '-'}
              </span>
            </div>

            {/* Containers on this booking */}
            <div className="flex items-start justify-between py-4 border-b border-gray-200 gap-4">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Containers
              </span>
              <div className="flex flex-col items-end gap-1">
                {(shipmentData.containerNumbers && shipmentData.containerNumbers.length > 0
                  ? shipmentData.containerNumbers
                  : shipmentData.containerNumber && shipmentData.containerNumber !== '—'
                    ? [shipmentData.containerNumber]
                    : []
                ).map((container) => (
                  <span
                    key={container}
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '22px',
                      color: '#212126'
                    }}
                  >
                    {container}
                  </span>
                ))}
                {(!shipmentData.containerNumbers || shipmentData.containerNumbers.length === 0) &&
                  (!shipmentData.containerNumber || shipmentData.containerNumber === '—') && (
                    <span
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '22px',
                        color: '#212126',
                      }}
                    >
                      —
                    </span>
                  )}
              </div>
            </div>

            {/* Vessel */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Vessel
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.vessel || '-'}
              </span>
            </div>

            {/* Shipping Line */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Shipping Line
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.shippingLine || '-'}
              </span>
            </div>

            {/* Product */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Product
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.product || '-'}
              </span>
            </div>

            {/* Customer */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Customer
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.customer || '-'}
              </span>
            </div>

            {/* Factory */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Factory
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.factory || '-'}
              </span>
            </div>

            {/* Origin */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Origin
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.origin || '-'}
              </span>
            </div>

            {/* Destination */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Destination
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.destination || '-'}
              </span>
            </div>

            {/* ETD */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                ETD
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.etd || '-'}
              </span>
            </div>

            {/* ETA */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                ETA
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.eta || '-'}
              </span>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between py-4">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Status
              </span>
              {statusPill}
            </div>

            {/* Document upload & list */}
            {shipmentData.id && (
              <div className="py-4 border-t border-gray-100">
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '22px',
                    color: '#212126'
                  }}
                >
                  Documents
                </span>

                {/* Existing documents */}
                <div className="mt-3">
                  {documentsLoading ? (
                    <div className="flex items-center gap-2 text-gray-500" style={{ fontFamily: 'Lexend Deca', fontSize: '13px' }}>
                      <FiLoader className="w-4 h-4 animate-spin" />
                      Loading documents…
                    </div>
                  ) : documents.length === 0 ? (
                    <p style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#69697A' }}>
                      No documents uploaded yet.
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <label className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                          <input
                            type="checkbox"
                            checked={documents.length > 0 && selectedDocIds.size === documents.length}
                            onChange={toggleSelectAllDocs}
                            className="rounded border-gray-300"
                          />
                          Select all
                        </label>
                        <button
                          type="button"
                          disabled={selectedDocIds.size === 0 || downloadingDocs}
                          onClick={handleDownloadSelected}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          style={{ fontFamily: 'Lexend Deca', fontWeight: 500, color: '#212126' }}
                        >
                          {downloadingDocs ? (
                            <>
                              <FiLoader className="w-3.5 h-3.5 animate-spin" />
                              Downloading…
                            </>
                          ) : (
                            <>
                              <FiDownload className="w-3.5 h-3.5" />
                              Download selected ({selectedDocIds.size})
                            </>
                          )}
                        </button>
                      </div>
                      <ul className="space-y-2 mb-3">
                        {documents.map((doc) => (
                          <li
                            key={doc.id}
                            className="flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={selectedDocIds.has(doc.id)}
                              onChange={() => toggleDocSelection(doc.id)}
                              className="rounded border-gray-300 shrink-0"
                              aria-label={`Select ${doc.name}`}
                            />
                            <span
                              className="flex-1 min-w-0 truncate"
                              style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#212126' }}
                              title={doc.name}
                            >
                              {doc.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => openDocumentById(doc.id).catch(() => setDownloadError('Could not open document'))}
                              className="shrink-0 px-2 py-1 rounded text-xs hover:bg-gray-200 transition-colors"
                              style={{ fontFamily: 'Lexend Deca', color: '#212126' }}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownloadOne(doc)}
                              className="shrink-0 p-1.5 rounded hover:bg-gray-200 transition-colors"
                              title="Download"
                              aria-label={`Download ${doc.name}`}
                            >
                              <FiDownload className="w-4 h-4 text-gray-700" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {downloadError && (
                    <p className="text-sm text-red-600 mb-2" style={{ fontFamily: 'Lexend Deca' }}>{downloadError}</p>
                  )}
                </div>

                {/* Upload */}
                <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3">
                  <input
                    key={fileInputKey}
                    type="file"
                    accept=".pdf,application/pdf"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? [])
                      setUploadFiles(files)
                      setUploadError(null)
                      setUploadSuccess(false)
                    }}
                    className="block w-full text-sm text-gray-600 border border-gray-200 rounded-lg p-2"
                    style={{ fontFamily: 'Lexend Deca' }}
                  />
                  {uploadFiles.length > 0 && (
                    <p style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                      {uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                  {uploadError && (
                    <p className="text-sm text-red-600" style={{ fontFamily: 'Lexend Deca' }}>{uploadError}</p>
                  )}
                  {uploadSuccess && (
                    <p className="text-sm text-green-600" style={{ fontFamily: 'Lexend Deca' }}>Document(s) uploaded successfully.</p>
                  )}
                  <button
                    type="button"
                    disabled={uploadFiles.length === 0 || uploading}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (!shipmentData.id || uploadFiles.length === 0 || uploading) return
                      setUploading(true)
                      setUploadError(null)
                      setUploadSuccess(false)
                      uploadShipmentDocuments(shipmentData.id, uploadFiles)
                        .then(() => {
                          setUploadFiles([])
                          setFileInputKey((k) => k + 1)
                          setUploadSuccess(true)
                          onDocumentUploaded?.()
                          return loadDocuments()
                        })
                        .catch((err: unknown) => {
                          const msg = (err as { response?: { data?: { error?: { message?: string }; message?: string } } })?.response?.data?.error?.message
                            ?? (err as Error)?.message
                            ?? 'Upload failed'
                          setUploadError(String(msg))
                        })
                        .finally(() => setUploading(false))
                    }}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm cursor-pointer"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 500,
                      color: '#212126',
                      alignSelf: 'flex-start'
                    }}
                    title={uploadFiles.length === 0 ? 'Select one or more PDF files first' : 'Upload document(s)'}
                  >
                    {uploading ? 'Uploading...' : uploadFiles.length > 1 ? `Upload ${uploadFiles.length} documents` : 'Upload document'}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500" style={{ fontFamily: 'Lexend Deca' }}>PDF only, max 100MB per file. Select multiple files to upload at once.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div 
          className="flex items-center justify-end gap-4 border-t border-gray-200 flex-shrink-0 px-4 sm:px-6"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#212126',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              height: '38px'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(
    <>
      {modalContent}
      <AISummaryModal
        isOpen={aiSummaryOpen}
        onClose={() => setAiSummaryOpen(false)}
        entityType="shipment"
        entityId={shipmentData.id ?? null}
      />
    </>,
    document.body
  )
}

export default ShipmentDetailsModal
